/**
 * Model image-input limits for node attachments.
 *
 * Uploads, File Manager picks, and connected upstream images share one plan.
 * The previous node upload sliced every selection to a fixed cap of 3 and
 * discarded the rest with no explanation, then always forwarded a generic
 * `images` array. This module keeps every selected asset and only builds the
 * fields the selected model actually declares.
 */

export const DEFAULT_IMAGE_FORMATS = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'] as const

export type ImageAttachmentSource = 'upload' | 'file-manager' | 'connection'
export type ImageAssetOrigin = 'local' | 'cloud'
export type ImageExecution = 'local' | 'cloud'
export type ImageInputRole = 'image' | 'reference' | 'mask' | 'control'

export interface ImageAttachment {
  id: string
  url: string
  source: ImageAttachmentSource
  origin: ImageAssetOrigin
  role?: string
  contentType?: string
  bytes?: number
  name?: string
  /** Public URL when the same bytes are also reachable by a cloud model. */
  cloudUrl?: string
}

export interface ImageInputSlot {
  name: string
  label: string
  role: ImageInputRole
  multiple: boolean
  /** Null when the model accepts an array and states no maximum. */
  maxCount: number | null
  required: boolean
  formats: string[]
  maxBytes?: number
}

export interface ImageInputCapability {
  declared: boolean
  acceptsImages: boolean
  multiple: boolean
  /** Null when at least one slot has no stated maximum. */
  maxCount: number | null
  slots: ImageInputSlot[]
  formats: string[]
  maxBytes?: number
}

export interface ImageInputPlan {
  /** Every attachment the user still has. Never a silently truncated subset. */
  retained: ImageAttachment[]
  /** Exact backend fields, or null when nothing may be sent. */
  fields: Record<string, string | string[]> | null
  explanation: string | null
  blocked: boolean
}

const EXCLUDED = new Set([
  'image_size',
  'num_images',
  'max_images',
  'number_of_images',
  'original_image_size',
  'initial_image_strength',
])

const NAME_PATTERN = /(?:mask|image|photo|img|face|person|garment|human|clothing|identity|product|reference|style|subject|control|canny|depth|pose|openpose).*(?:_url|_urls|_path)$|^(?:image|input_image|source_image|reference_image|style_image|subject_image|mask|mask_image|control_image|controlnet_image)$/i

export function classifyAssetOrigin(url: string): ImageAssetOrigin {
  return isCloudAccessible(url) ? 'cloud' : 'local'
}

export function isCloudAccessible(url: string): boolean {
  if (!url.startsWith('https://')) return false
  let hostname = ''
  try {
    hostname = new URL(url).hostname.toLowerCase()
  } catch {
    return false
  }
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1' || hostname.endsWith('.local')) return false
  const ipv4 = hostname.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/)
  if (ipv4) {
    const a = Number(ipv4[1])
    const b = Number(ipv4[2])
    if (a === 10 || a === 127 || a === 0 || (a === 172 && b >= 16 && b <= 31) || (a === 192 && b === 168) || (a === 169 && b === 254)) return false
  }
  return true
}

export function urlForExecution(attachment: ImageAttachment, execution: ImageExecution): string {
  if (execution === 'local') return attachment.url
  if (attachment.cloudUrl && isCloudAccessible(attachment.cloudUrl)) return attachment.cloudUrl
  return attachment.url
}

export function executionForProvider(provider: string | undefined | null): ImageExecution {
  return provider === 'local' ? 'local' : 'cloud'
}

export function attachmentFromUrl(
  url: string,
  source: ImageAttachmentSource,
  extra: Partial<Omit<ImageAttachment, 'id' | 'url' | 'source' | 'origin'>> = {},
): ImageAttachment {
  return {
    id: url,
    url,
    source,
    origin: classifyAssetOrigin(extra.cloudUrl && isCloudAccessible(extra.cloudUrl) ? extra.cloudUrl : url),
    ...extra,
  }
}

export interface StoredImageAsset {
  name?: string
  contentType?: string
  bytes?: number
  cloudUrl?: string
}

/** Rebuild attachments from the URLs saved on the node plus the sidecar recorded at pick time. */
export function hydrateAttachments(
  urls: readonly string[],
  assets: Readonly<Record<string, StoredImageAsset>> | undefined,
  roles: Readonly<Record<string, string>> | undefined,
  connected: readonly (string | Pick<ImageAttachment, 'url' | 'role'>)[] = [],
): ImageAttachment[] {
  const selected = urls.filter(url => typeof url === 'string' && url.length > 0)
  const connections = connected.map(item => typeof item === 'string' ? { url: item, role: roles?.[item] } : item)
  const wired = new Set(connections.map(item => item.url))
  const attachments = [
    ...selected.filter(url => !wired.has(url)).map(url => attachmentFromUrl(url, 'upload', { ...(assets?.[url] ?? {}), role: roles?.[url] })),
    ...connections.map(({ url, role }) => attachmentFromUrl(url, 'connection', { name: url.split('/').pop(), ...(assets?.[url] ?? {}), role: role ?? roles?.[url] })),
  ]
  // One picture can legitimately fill two distinct ports; deduplicate by role as well as URL.
  return attachments.filter((item, index) => attachments.findIndex(other => other.url === item.url && other.role === item.role) === index)
}

/** Preserve the input parameter attached to each wire through the same validation used by uploads. */
export function connectedImageAttachments(
  capability: ImageInputCapability,
  fields: Readonly<Record<string, unknown>>,
  fallbackUrls: readonly string[],
): Pick<ImageAttachment, 'url' | 'role'>[] {
  const assigned = capability.slots.flatMap(slot => {
    const value = fields[slot.name]
    const values = Array.isArray(value) ? value : [value]
    return values.filter((url): url is string => typeof url === 'string' && url.length > 0)
      .map(url => ({ url, role: slot.name }))
  })
  const known = new Set(assigned.map(item => item.url))
  return [...assigned, ...fallbackUrls.filter(url => !known.has(url)).map(url => ({ url }))]
}

export function resolveImageInputCapability(model: {
  parameterSchema?: unknown
  parameterOverrides?: unknown
  purpose?: string | null
} | null | undefined): ImageInputCapability {
  const extracted = extractSchema(model?.parameterSchema)
  const slots = slotsFromSchema(extracted, overridesOf(model?.parameterOverrides))
  const declared = extracted.declared
  const maxCount = slots.reduce<number | null>((sum, slot) => {
    if (sum === null || slot.maxCount === null) return null
    return sum + slot.maxCount
  }, 0)
  const formats = unique(slots.flatMap(slot => slot.formats))
  const maxBytes = slots.map(slot => slot.maxBytes).find(value => value !== undefined)
  return {
    declared,
    acceptsImages: slots.length > 0,
    multiple: slots.some(slot => slot.multiple) || slots.length > 1,
    maxCount: slots.length === 0 ? 0 : maxCount,
    slots,
    formats: formats.length > 0 ? formats : [...DEFAULT_IMAGE_FORMATS],
    maxBytes,
  }
}

export function admitImageSelection(
  capability: ImageInputCapability,
  current: readonly ImageAttachment[],
  incoming: readonly ImageAttachment[],
): { attachments: ImageAttachment[]; explanation: string | null } {
  const attachments = current.map(item => ({ ...item }))
  const seen = new Set(attachments.map(item => item.url))
  for (const item of incoming) {
    if (seen.has(item.url)) continue
    attachments.push({ ...item })
    seen.add(item.url)
  }
  return { attachments, explanation: selectionExplanation(capability, attachments) }
}

export function planImageInputs(
  capability: ImageInputCapability,
  attachments: readonly ImageAttachment[],
  execution: ImageExecution,
): ImageInputPlan {
  const retained = attachments.map(item => ({ ...item }))
  if (retained.length === 0 && !capability.slots.some(slot => slot.required)) {
    return { retained, fields: null, blocked: false, explanation: null }
  }
  if (!capability.declared) {
    return {
      retained,
      fields: null,
      blocked: true,
      explanation: 'This model\'s image inputs are not declared, so attached images will not be sent. Choose a model that describes its image inputs, or remove the images. They were not dropped.',
    }
  }
  if (!capability.acceptsImages) {
    return {
      retained,
      fields: null,
      blocked: true,
      explanation: 'This model does not accept images. Remove the attached images before running, or switch to a model that does. They were not dropped.',
    }
  }

  const problems: string[] = []
  for (const slot of capability.slots) {
    if (slot.required && !retained.some(item => capability.slots.length === 1 || item.role === slot.name)) {
      problems.push(`Connect or choose ${slot.label} before running.`)
    }
  }
  for (const attachment of retained) {
    const type = normalizeMime(attachment.contentType)
    if (type && !capability.formats.includes(type)) {
      problems.push(`“${labelOf(attachment)}” is ${type}, which this model does not accept. Use ${formatList(capability.formats)}.`)
    }
    if (attachment.bytes !== undefined && capability.maxBytes !== undefined && attachment.bytes > capability.maxBytes) {
      problems.push(`“${labelOf(attachment)}” is larger than this model allows (${formatBytes(capability.maxBytes)}). Use a smaller image. It stays attached.`)
    }
    if (execution === 'cloud' && !isCloudAccessible(urlForExecution(attachment, 'cloud'))) {
      problems.push(`Cloud execution cannot read “${labelOf(attachment)}”. It is only on this device. Run a local model, or choose a file with a public URL. It stays attached.`)
    }
  }

  if (capability.slots.length > 1) {
    const unassigned = retained.filter(item => !item.role || !capability.slots.some(slot => slot.name === item.role))
    if (unassigned.length > 0) {
      problems.push(`Assign a role before running. This model needs ${capability.slots.map(slot => slot.label).join(' and ')}. The images stay attached.`)
    }
    for (const slot of capability.slots) {
      const assigned = retained.filter(item => item.role === slot.name)
      if (slot.maxCount !== null && assigned.length > slot.maxCount) {
        problems.push(`${slot.label} accepts ${countLabel(slot.maxCount)}. ${assigned.length} are assigned and were not dropped. Remove the extras before running.`)
      }
    }
  } else {
    const slot = capability.slots[0]
    if (slot?.maxCount !== null && slot && retained.length > slot.maxCount) {
      problems.push(`This model accepts ${countLabel(slot.maxCount)}. ${retained.length} are attached and were not dropped. Remove the extras before running.`)
    }
  }

  if (problems.length > 0) {
    return { retained, fields: null, blocked: true, explanation: problems.join(' ') }
  }

  const fields: Record<string, string | string[]> = {}
  if (capability.slots.length === 1) {
    const slot = capability.slots[0]
    const urls = retained.map(item => urlForExecution(item, execution))
    fields[slot.name] = slot.multiple ? urls : urls[0]
  } else {
    for (const slot of capability.slots) {
      const urls = retained.filter(item => item.role === slot.name).map(item => urlForExecution(item, execution))
      if (urls.length === 0) continue
      fields[slot.name] = slot.multiple ? urls : urls[0]
    }
  }
  return { retained, fields, blocked: false, explanation: null }
}

export function slotLabel(name: string): string {
  const words = name
    .replace(/_urls?$/, '')
    .replace(/_path$/, '')
    .split('_')
    .filter(Boolean)
  if (words.length === 0) return 'Image'
  return words.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
}

function selectionExplanation(capability: ImageInputCapability, attachments: readonly ImageAttachment[]): string | null {
  if (!capability.declared || !capability.acceptsImages || capability.slots.length !== 1) return null
  const slot = capability.slots[0]
  if (slot.maxCount === null || attachments.length <= slot.maxCount) return null
  return `This model accepts ${countLabel(slot.maxCount)}. ${attachments.length} are attached and were not dropped. Remove the extras before running.`
}

function slotsFromSchema(
  extracted: { properties: Record<string, Record<string, unknown>>; required: string[] },
  overrides: Record<string, Record<string, unknown>>,
): ImageInputSlot[] {
  const names = new Set([...Object.keys(extracted.properties), ...Object.keys(overrides)])
  const slots: ImageInputSlot[] = []
  for (const name of names) {
    if (name.startsWith('__')) continue
    const def = extracted.properties[name] ?? {}
    const override = overrides[name] ?? {}
    if (!isImageSlot(name, def, override)) continue
    const multiple = def.type === 'array' || /_urls$|_paths$/i.test(name) || (typeof override.maxHandles === 'number' && override.maxHandles > 1)
    const stated = firstNumber(override.maxHandles, def.maxItems, def.maximum, def.max, arrayItemLimit(def))
    slots.push({
      name,
      label: typeof override.title === 'string' ? override.title : typeof def.title === 'string' ? def.title : slotLabel(name),
      role: roleFor(name, { ...def, handleType: override.inputType || def.handleType }),
      multiple,
      maxCount: multiple ? stated : 1,
      required: def.required === true || extracted.required.includes(name),
      formats: formatsFor(def),
      maxBytes: bytesFor(def),
    })
  }
  return slots
}

function extractSchema(schema: unknown): { declared: boolean; properties: Record<string, Record<string, unknown>>; required: string[] } {
  if (!schema || typeof schema !== 'object' || Array.isArray(schema)) {
    return { declared: false, properties: {}, required: [] }
  }
  const record = schema as Record<string, unknown>
  if (Array.isArray(record.inputs) || Array.isArray(record.settings)) {
    const properties: Record<string, Record<string, unknown>> = {}
    const required: string[] = []
    const entries = [...(record.inputs as unknown[]), ...(Array.isArray(record.settings) ? record.settings as unknown[] : [])]
    for (const entry of entries) {
      if (!entry || typeof entry !== 'object') continue
      const item = entry as Record<string, unknown>
      const name = typeof item.name === 'string' ? item.name : ''
      if (!name) continue
      properties[name] = item
      if (item.required === true) required.push(name)
    }
    return { declared: true, properties, required }
  }
  if (record.properties && typeof record.properties === 'object' && !Array.isArray(record.properties) && !record.components && !record.paths) {
    return { declared: true, properties: asProperties(record.properties), required: stringList(record.required) }
  }
  if (record.components && typeof record.components === 'object') {
    const openApi = openApiInput(record)
    if (openApi) return { declared: true, ...openApi }
  }
  const first = Object.values(record)[0]
  if (first && typeof first === 'object' && !Array.isArray(first) && 'type' in (first as Record<string, unknown>)) {
    return { declared: true, properties: asProperties(record), required: [] }
  }
  return { declared: false, properties: {}, required: [] }
}

function openApiInput(schema: Record<string, unknown>): { properties: Record<string, Record<string, unknown>>; required: string[] } | null {
  const components = schema.components as Record<string, unknown>
  const schemas = components.schemas as Record<string, Record<string, unknown>> | undefined
  if (!schemas) return null
  const paths = schema.paths as Record<string, Record<string, unknown>> | undefined
  const firstPath = paths ? Object.values(paths)[0] : undefined
  const post = firstPath?.post as Record<string, unknown> | undefined
  const content = (post?.requestBody as Record<string, unknown> | undefined)?.content as Record<string, Record<string, unknown>> | undefined
  const ref = content?.['application/json']?.schema as Record<string, string> | undefined
  const refName = ref?.$ref?.split('/').pop()
  const named = refName && schemas[refName]?.properties
    ? schemas[refName]
    : Object.entries(schemas).find(([name, def]) => name.includes('Input') && def.properties)?.[1]
  if (!named?.properties || typeof named.properties !== 'object') return null
  return { properties: asProperties(named.properties), required: stringList(named.required) }
}

function asProperties(value: unknown): Record<string, Record<string, unknown>> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).flatMap(([name, item]) => (
      item && typeof item === 'object' ? [[name, item as Record<string, unknown>]] : []
    )),
  )
}

function overridesOf(value: unknown): Record<string, Record<string, unknown>> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).flatMap(([name, item]) => (
      item && typeof item === 'object' ? [[name, item as Record<string, unknown>]] : []
    )),
  )
}

function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []
}

function arrayItemLimit(def: Record<string, unknown>): number | null {
  const items = def.items
  if (!items || typeof items !== 'object' || Array.isArray(items)) return null
  return firstNumber((items as Record<string, unknown>).maxItems, (items as Record<string, unknown>).maximum)
}

function isImageSlot(name: string, def: Record<string, unknown>, override: Record<string, unknown>): boolean {
  if (EXCLUDED.has(name)) return false
  const inputType = String(override.inputType || override.handleType || def.handleType || def.inputType || '')
  if (inputType === 'text' || inputType === 'video' || inputType === 'audio' || inputType === 'mesh') return false
  if (inputType === 'image' || inputType === 'reference' || inputType === 'mask' || inputType === 'controlnet') return true
  return NAME_PATTERN.test(name)
}

function roleFor(name: string, def: Record<string, unknown>): ImageInputRole {
  const handle = String(def.handleType || '')
  const haystack = `${name} ${handle}`.toLowerCase()
  if (haystack.includes('mask')) return 'mask'
  if (/control|canny|depth|pose|openpose/.test(haystack)) return 'control'
  if (/reference|style|subject/.test(haystack)) return 'reference'
  return 'image'
}

function formatsFor(def: Record<string, unknown>): string[] {
  const listed = [def.contentMediaType, ...(Array.isArray(def.enum) ? def.enum : [])]
    .filter((value): value is string => typeof value === 'string' && value.startsWith('image/'))
    .map(normalizeMime)
    .filter((value): value is string => value !== null)
  return listed.length > 0 ? unique(listed) : [...DEFAULT_IMAGE_FORMATS]
}

function bytesFor(def: Record<string, unknown>): number | undefined {
  if (typeof def.maxBytes === 'number') return def.maxBytes
  const description = typeof def.description === 'string' ? def.description : ''
  const match = description.match(/(\d+(?:\.\d+)?)\s*MB/i)
  return match ? Math.round(Number(match[1]) * 1024 * 1024) : undefined
}

function normalizeMime(value: string | undefined): string | null {
  if (!value) return null
  const mime = value.split(';')[0].trim().toLowerCase()
  if (mime === 'image/jpg') return 'image/jpeg'
  return mime.startsWith('image/') ? mime : null
}

function formatList(formats: readonly string[]): string {
  const names = formats.map(format => format.replace('image/', '').replace('jpeg', 'JPEG').toUpperCase())
  if (names.length <= 1) return names[0] ?? 'a supported image'
  return `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`
}

function formatBytes(bytes: number): string {
  const mb = bytes / (1024 * 1024)
  return Number.isInteger(mb) ? `${mb} MB` : `${mb.toFixed(1)} MB`
}

function countLabel(count: number): string {
  return count === 1 ? '1 image' : `${count} images`
}

function labelOf(attachment: ImageAttachment): string {
  return attachment.name || 'This image'
}

function firstNumber(...values: unknown[]): number | null {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value) && value >= 0) return value
  }
  return null
}

function unique(values: string[]): string[] {
  return [...new Set(values)]
}
